import { useState, useCallback, useRef } from 'react';
import type { Message, Job } from '../types';
import { getApiUrl } from '../config';

export function useChat() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentJob, setCurrentJob] = useState<Job | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  /**
   * Send a message and stream the response from Claude via SSE.
   */
  const sendMessage = useCallback(async (content: string, attachments: any[] = []) => {
    if (!content.trim() && attachments.length === 0) return;

    // Cancel any in-flight request
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    // Convert File objects to base64 before sending
    const processedAttachments = await Promise.all(
      attachments.map(async (att) => {
        if (att.type === 'image' && att.file) {
          const base64 = await fileToBase64(att.file);
          return { id: att.id, type: 'image', base64, mediaType: att.file.type };
        }
        if (att.key) {
          return { id: att.id, type: att.type, key: att.key, mediaType: att.mediaType };
        }
        return att;
      })
    );

    // Add user message immediately
    const userMessage: Message = {
      id: crypto.randomUUID(),
      role: 'user',
      content,
      timestamp: new Date(),
      attachments: attachments.map(att => ({
        id: att.id,
        type: att.type,
        name: att.file?.name ?? att.name ?? 'file',
        url: att.file ? URL.createObjectURL(att.file) : (att.url ?? ''),
      })),
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    // Placeholder assistant message that we'll update as chunks arrive
    const assistantId = crypto.randomUUID();
    const assistantMessage: Message = {
      id: assistantId,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      streaming: true,
    };
    setMessages(prev => [...prev, assistantMessage]);

    try {
      // Build history from current messages (exclude the placeholder we just added)
      const history = messages.map(m => ({
        role: m.role,
        content: m.content,
      }));

      const response = await fetch(getApiUrl('/chat/stream'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content,
          history,
          attachments: processedAttachments,
        }),
        signal: controller.signal,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API error ${response.status}: ${errorText.substring(0, 200)}`);
      }

      if (!response.body) {
        throw new Error('No response body');
      }

      // Read the SSE stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let fullText = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6).trim();
          if (!data) continue;

          try {
            const event = JSON.parse(data);

            if (event.type === 'chunk') {
              fullText += event.text;
              // Update the streaming message in real-time
              setMessages(prev =>
                prev.map(m =>
                  m.id === assistantId
                    ? { ...m, content: fullText, streaming: true }
                    : m
                )
              );
            }

            if (event.type === 'done') {
              // Finalize the message
              setMessages(prev =>
                prev.map(m =>
                  m.id === assistantId
                    ? {
                        ...m,
                        content: fullText,
                        streaming: false,
                        patternGenerated: event.patternGenerated ?? false,
                        garmentSpec: event.garmentSpec ?? null,
                      }
                    : m
                )
              );
            }

            if (event.type === 'error') {
              throw new Error(event.message);
            }
          } catch (parseErr) {
            // Skip malformed SSE lines
          }
        }
      }

      // Ensure streaming flag is cleared even if 'done' event was missed
      setMessages(prev =>
        prev.map(m =>
          m.id === assistantId && m.streaming
            ? { ...m, streaming: false }
            : m
        )
      );
    } catch (err: any) {
      if (err.name === 'AbortError') {
        // Request was cancelled — remove the placeholder
        setMessages(prev => prev.filter(m => m.id !== assistantId));
        return;
      }

      console.error('[useChat] Stream error:', err);

      // Show error in the assistant message
      setMessages(prev =>
        prev.map(m =>
          m.id === assistantId
            ? {
                ...m,
                content: "I'm having trouble connecting right now. Please try again in a moment.",
                streaming: false,
              }
            : m
        )
      );
    } finally {
      setIsLoading(false);
      setCurrentJob(null);
    }
  }, [messages]);

  /**
   * Upload a file (returns metadata for attaching to a message).
   */
  const uploadFile = useCallback(async (file: File) => {
    return {
      id: crypto.randomUUID(),
      type: file.type.startsWith('image/') ? 'image' : 'document',
      file,
      mediaType: file.type,
      url: URL.createObjectURL(file),
    };
  }, []);

  return {
    messages,
    isLoading,
    currentJob,
    sendMessage,
    uploadFile,
    uploadedFile: null,
  };
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Strip the data URL prefix (e.g. "data:image/jpeg;base64,")
      resolve(result.split(',')[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
